import { CLAIM_STATUS } from "../constants";
import type { Claim, ClaimTranslator, StepperStep } from "../types";
import { isAwaitingReview } from "./claimState";
import {
  AWAITING_REVIEW_STEPS,
  RESOLUTION_STEP_KEYS,
  TIMELINE_ALIASES,
} from "./stepperConfig";

type StepperContext = {
  claim: Claim;
  timelineMap: Record<string, string>;
  stepKeys: string[];
};

const getStepDate = (
  stepKey: string,
  timelineMap: Record<string, string>,
): string | null => {
  const aliases = TIMELINE_ALIASES[stepKey] ?? [stepKey];
  for (const alias of aliases) {
    if (timelineMap[alias]) {
      return timelineMap[alias];
    }
  }
  return null;
};

const getPrimaryReshipShipment = (claim: Claim) =>
  claim.resolution?.reships?.[0]?.shipments?.[0] ?? null;

const isStepCompleted = (
  stepKey: string,
  timelineMap: Record<string, string>,
  claim: Claim,
): boolean => {
  if (stepKey === "decision") {
    return (
      Boolean(timelineMap.approved) ||
      Boolean(timelineMap.denied) ||
      claim.claim_status === CLAIM_STATUS.APPROVED ||
      claim.claim_status === CLAIM_STATUS.CLOSED ||
      claim.claim_status === CLAIM_STATUS.DENIED
    );
  }

  if (stepKey === "resolved") {
    return (
      claim.claim_status === CLAIM_STATUS.CLOSED ||
      Boolean(timelineMap.closed) ||
      Boolean(timelineMap.refund_issued) ||
      Boolean(timelineMap.store_credit_applied) ||
      Boolean(timelineMap.reship_delivered)
    );
  }

  if (timelineMap[stepKey]) {
    return true;
  }

  const shipment = getPrimaryReshipShipment(claim);

  switch (stepKey) {
    case "claim_filed":
      return Boolean(timelineMap.claim_filed);
    case "refund_issued":
      return Boolean(timelineMap.refund_issued);
    case "store_credit_applied":
      return Boolean(timelineMap.store_credit_applied);
    case "reship_created":
      return (
        Boolean(timelineMap.reship_created) ||
        Boolean(timelineMap.reship_shipped) ||
        Boolean(timelineMap.reship_delivered) ||
        Boolean(claim.resolution?.reships?.length)
      );
    case "reship_shipped":
      return (
        Boolean(timelineMap.reship_shipped) ||
        Boolean(timelineMap.reship_delivered) ||
        Boolean(shipment?.tracking_number) ||
        Boolean(shipment?.delivered_at)
      );
    case "reship_delivered":
      return (
        Boolean(timelineMap.reship_delivered) || Boolean(shipment?.delivered_at)
      );
    default:
      return false;
  }
};

const getStepState = (
  stepKey: string,
  stepIndex: number,
  { claim, timelineMap, stepKeys }: StepperContext,
): StepperStep["state"] => {
  if (claim.claim_status === CLAIM_STATUS.DENIED) {
    return stepKey === "claim_filed" || stepKey === "decision"
      ? "completed"
      : "inactive";
  }

  if (isStepCompleted(stepKey, timelineMap, claim)) {
    return "completed";
  }

  if (isAwaitingReview(claim)) {
    return stepKey === "decision" ? "current" : "inactive";
  }

  const firstIncompleteIndex = stepKeys.findIndex(
    (key) => !isStepCompleted(key, timelineMap, claim),
  );

  return firstIncompleteIndex === stepIndex ? "current" : "inactive";
};

const getStepSubtextKey = (
  stepKey: string,
  stepState: StepperStep["state"],
  claim: Claim,
): string | null => {
  if (stepState === "current" || stepState === "completed") {
    return null;
  }

  if (isAwaitingReview(claim) && stepKey === "resolved") {
    return "ifApproved";
  }

  if (
    stepKey === "resolved" ||
    stepKey.startsWith("reship_") ||
    stepKey === "refund_issued" ||
    stepKey === "store_credit_applied"
  ) {
    return "afterDecision";
  }

  return null;
};

const getVisibleStepKeys = (resolutionType: string, claim: Claim): string[] => {
  if (isAwaitingReview(claim)) {
    return [...AWAITING_REVIEW_STEPS];
  }

  if (claim.claim_status === CLAIM_STATUS.DENIED) {
    return ["claim_filed", "decision"];
  }

  return [
    ...(RESOLUTION_STEP_KEYS[resolutionType] ?? RESOLUTION_STEP_KEYS.default),
  ];
};

export const getStepperSteps = (
  claim: Claim,
  resolutionType: string,
  timelineMap: Record<string, string>,
  t: ClaimTranslator,
): StepperStep[] => {
  const stepKeys = getVisibleStepKeys(resolutionType, claim);
  const context: StepperContext = { claim, timelineMap, stepKeys };

  return stepKeys.map((stepKey, index) => {
    const state = getStepState(stepKey, index, context);

    return {
      key: stepKey,
      label: t(`status.stepper.${stepKey}`),
      date: getStepDate(stepKey, timelineMap),
      state,
      subtextKey: getStepSubtextKey(stepKey, state, claim),
    };
  });
};
